import { useMemo } from "react";
import dayjs from "dayjs";
import VideoDuration from "layouts/components/VideoDuration"; // Absolute path preferred for clarity

const FREQUENCY_RATE = 1.03;

export const useEntityMetrics = (entityObject) => {
    return useMemo(() => {
        if (!entityObject) return { profileObject: {}, metrics: {} };

        const profile = entityObject.profile || {};
        const platform = entityObject.profile?.platform || entityObject.platform;

        let allReactions = 0;
        let engagement = 0;
        let comments = 0;
        let shares = 0;
        let likes = 0;
        let views = 0;
        let videoLength = 0;

        const postMetrics = entityObject.postMetrics || {};
        const videoMetrics = entityObject.videoMetrics || {};

        function formatDuration(totalSeconds) {
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            const parts = [];
            if (hours > 0) parts.push(`${hours}hr`);
            if (minutes > 0) parts.push(`${minutes}m`);
            parts.push(`${seconds}s`);
            return parts.join(" ");
        }

        // ---------------- Platform Logic ----------------
        if (platform === "Facebook") {
            allReactions =
                (postMetrics.likes || postMetrics.like || 0) +
                (postMetrics.love || 0) +
                (postMetrics.haha || 0) +
                (postMetrics.wow || 0) +
                (postMetrics.sad || 0) +
                (postMetrics.angry || 0);

            comments = postMetrics.comments || 0;
            shares = postMetrics.shares || 0;
            views = postMetrics.views || postMetrics.post_views || 0;
            engagement = allReactions + comments + shares;
        } else if (platform === "YouTube") {
            likes = videoMetrics.likeCount || 0;
            comments = videoMetrics.commentCount || 0;
            views = videoMetrics.viewCount || 0;
            videoLength = videoMetrics.duration
                ? formatDuration(videoMetrics.duration)
                : "NA";
            engagement = likes + comments;
        } else if (platform === "Instagram") {
            likes = postMetrics.likes || 0;
            comments = postMetrics.comments || 0;
            views = postMetrics.views || 0;
            videoLength = postMetrics.videoLength || 0;
            engagement = likes + comments;
        }

        // ---------------- Profile Followers ----------------
        const postDate = dayjs(
            entityObject.created_time ||
            entityObject.publishedAt ||
            entityObject.taken_at
        ).format("YYYY-MM-DD");

        const profileDataForDate = profile.data?.find(
            (entry) => dayjs(entry.date).format("YYYY-MM-DD") === postDate
        );

        const followersCount =
            profileDataForDate?.followers ||
            profileDataForDate?.subscribers ||
            profileDataForDate?.predicted_followers ||
            profileDataForDate?.predicted_subscribers ||
            0;

        // ==============================================================
        //     ACTUAL REACH / IMPRESSIONS OVERRIDE
        // ==============================================================
        let estimatedReach = null;
        let estimatedImpressions = null;

        // --------------------------------------------
        // Instagram actual metrics
        // --------------------------------------------
        if (postMetrics?.reach != null) {
            estimatedReach = postMetrics.reach;           // IG Reach
        }

        if (postMetrics?.impressions != null) {
            estimatedImpressions = postMetrics.impressions; // IG Impressions
        }

        // --------------------------------------------
        // FACEBOOK actual metrics
        // --------------------------------------------
        if (postMetrics?.post_impressions != null) {
            estimatedImpressions = postMetrics.post_impressions;
        } else if (postMetrics?.post_media_view != null) {
            estimatedImpressions = postMetrics.post_media_view;
        }

        if (postMetrics?.post_impressions_unique != null) {
            estimatedReach = postMetrics.post_impressions_unique;
        }

        // --------------------------------------------
        // BACKEND ESTIMATED METRICS (Competitors)
        // --------------------------------------------
        // If we don't have actuals, check for backend estimates
        if (estimatedReach == null && postMetrics?.estimated_post_reach != null) {
            estimatedReach = postMetrics.estimated_post_reach;
        }

        if (estimatedImpressions == null && postMetrics?.estimated_post_views != null) {
            estimatedImpressions = postMetrics.estimated_post_views;
        }

        // If both actual exist → done
        if (estimatedReach != null && estimatedImpressions != null) {
            // no estimation needed
        }
        // Only reach exists → compute impressions
        else if (estimatedReach != null) {
            estimatedImpressions = Math.round(estimatedReach * FREQUENCY_RATE);
        }
        // Only impressions exist → compute reach
        else if (estimatedImpressions != null) {
            estimatedReach = Math.round(estimatedImpressions / FREQUENCY_RATE);
        }
        // Otherwise → fallback to estimation logic (IF BACKEND FAILED TO POPULATE)
        else if (views > 0) {
            estimatedReach = Math.round(views / FREQUENCY_RATE);
            estimatedImpressions = Math.round(estimatedReach * FREQUENCY_RATE);
        } else {
            const minRate = 0.001;
            const maxRate = 0.03;
            const maxRateLimit = 1;

            const engByFollowers = followersCount ? engagement / followersCount : 0;
            const sharesByFollowers = followersCount ? shares / followersCount : 0;

            const performanceScore = Math.min(
                1,
                engByFollowers * 10 + sharesByFollowers * 20
            );

            const baseReachRate =
                minRate + performanceScore * (maxRate - minRate);

            const organicReachRate = Math.min(
                baseReachRate +
                engByFollowers * 20 +
                sharesByFollowers * 30,
                maxRateLimit
            );

            estimatedReach = Math.round(followersCount * organicReachRate);
            estimatedImpressions = Math.round(estimatedReach * FREQUENCY_RATE);
        }

        // ---------------- Engagement Rates ----------------
        const engRateByFollowers =
            followersCount > 0 ? (engagement / followersCount) * 100 : 0;

        const engRateByReach =
            estimatedReach > 0 ? (engagement / estimatedReach) * 100 : 0;

        const engRateByImpressions =
            estimatedImpressions > 0
                ? (engagement / estimatedImpressions) * 100
                : 0;

        const engRateByViews =
            views > 0 ? (engagement / views) * 100 : 0;

        // ==============================================================
        //                 DYNAMIC METRIC LABEL FIX
        // ==============================================================

        const hasActualReach =
            postMetrics?.post_impressions_unique != null ||  // FB
            postMetrics?.reach != null;                       // IG

        const hasActualImpressions =
            postMetrics?.post_impressions != null ||          // FB (Legacy)
            postMetrics?.post_media_view != null ||           // FB (New)
            postMetrics?.impressions != null ||               // IG
            (platform === "YouTube" && videoMetrics?.viewCount != null) || // YouTube
            (views > 0); // Generic fallback if we have simple views


        const reachLabel = hasActualReach ? "Reach" : "Estimated Reach";
        const impressionsLabel = hasActualImpressions ? "Views" : "Estimated Views";

        // ==============================================================
        //            BUILD METRICS FOR UI
        // ==============================================================

        const dynamicMetrics = {};

        if (platform === "YouTube") {
            dynamicMetrics["Likes"] = likes;
            dynamicMetrics["Comments"] = comments;
            dynamicMetrics["Views"] = views;
            dynamicMetrics[reachLabel] = estimatedReach;
            dynamicMetrics["Video Length"] = videoLength;
            dynamicMetrics["Engagement"] = engagement;
            dynamicMetrics["Eng. Rate by Subscribers"] = engRateByFollowers;
            dynamicMetrics["Eng. Rate by Reach"] = engRateByReach;
            dynamicMetrics["Eng. Rate by Views"] = engRateByViews;
        } else if (platform === "Facebook") {
            dynamicMetrics["Reactions"] = allReactions;
            dynamicMetrics["Comments"] = comments;
            dynamicMetrics["Shares"] = shares;
            dynamicMetrics[reachLabel] = estimatedReach;

            if (postMetrics.post_video_views > 0) {
                dynamicMetrics["Video Views"] = postMetrics.post_video_views;
            }

            if (postMetrics.post_views > 0 && postMetrics.post_views !== views) {
                dynamicMetrics["Post Views"] = postMetrics.post_views;
            }

            if (views > 0)
                dynamicMetrics["Views"] = views;
            else
                dynamicMetrics[impressionsLabel] = estimatedImpressions;

            dynamicMetrics["Engagement"] = engagement;
            dynamicMetrics["Eng. Rate by Followers"] = engRateByFollowers;
            dynamicMetrics["Eng. Rate by Reach"] = engRateByReach;

            if (views > 0)
                dynamicMetrics["Eng. Rate by Views"] = engRateByViews;
            else
                dynamicMetrics["Eng. Rate by Views"] = engRateByImpressions;
        } else if (platform === "Instagram") {
            dynamicMetrics["Likes"] = likes;
            dynamicMetrics["Comments"] = comments;
            dynamicMetrics[reachLabel] = estimatedReach;

            if (views > 0) {
                dynamicMetrics["Views"] = views;
                dynamicMetrics["Video Length"] = (
                    <VideoDuration src={entityObject.media_url} />
                );
            } else {
                dynamicMetrics[impressionsLabel] = estimatedImpressions;
            }

            dynamicMetrics["Engagement"] = engagement;
            dynamicMetrics["Eng. Rate by Followers"] = engRateByFollowers;
            dynamicMetrics["Eng. Rate by Reach"] = engRateByReach;

            if (views > 0)
                dynamicMetrics["Eng. Rate by Views"] = engRateByViews;
            else
                dynamicMetrics["Eng. Rate by Views"] = engRateByImpressions;
        }

        return {
            profileObject: profile,
            metrics: dynamicMetrics,
            reachLabel,
            impressionsLabel,
            hasActualReach,
            hasActualImpressions
        };
    }, [entityObject]);
};
